/**
 * Forecast48hModal - Shows 48h hourly weather forecast
 * Displays 1-hour slots updated twice daily
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { X, CloudRain, Thermometer, Droplets, Wind, Clock, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Forecast48hModal = ({ isOpen, onClose }) => {
  const { getAuthHeaders } = useAuth();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadForecast();
    }
  }, [isOpen]);

  const loadForecast = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/weather/forecast`, { headers: getAuthHeaders() });
      setForecast(res.data);
    } catch (err) {
      setError('Failed to load forecast data');
      console.error('Forecast error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const hourly = forecast?.timelines?.hourly || [];
  const hourly48 = hourly.slice(0, 48);

  // Group by day
  const groupedByDay = {};
  hourly48.forEach(h => {
    const date = new Date(h.time);
    const dayKey = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (!groupedByDay[dayKey]) groupedByDay[dayKey] = [];
    groupedByDay[dayKey].push(h);
  });

  return (
    <div 
      data-testid="forecast-48h-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        padding: 16
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="card-floating"
        style={{
          width: '100%',
          maxWidth: 800,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--card-bg)',
          borderRadius: 20,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-soft)',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(198,170,88,0.15)',
              borderRadius: 12,
              color: 'var(--gold)'
            }}>
              <Clock size={20} />
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>48-Hour Forecast</h2>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                Hourly weather data • Updated twice daily
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={loadForecast}
              disabled={loading}
              data-testid="refresh-forecast-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-soft)',
                borderRadius: 10,
                color: 'var(--text-secondary)',
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              <RefreshCw size={14} className={loading ? 'spin' : ''} />
              Refresh
            </button>
            <button
              onClick={onClose}
              data-testid="close-forecast-modal"
              style={{
                width: 36, height: 36,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-soft)',
                borderRadius: 10,
                color: 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              <div className="spinner" />
            </div>
          ) : error ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40, color: 'var(--text-muted)' }}>
              <CloudRain size={40} />
              <p>{error}</p>
              <button className="btn-depth-secondary" onClick={loadForecast}>Try Again</button>
            </div>
          ) : hourly48.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40, color: 'var(--text-muted)' }}>
              <CloudRain size={40} />
              <p>Forecast data not available yet</p>
              <p style={{ fontSize: 12 }}>Data is fetched twice daily at 8AM and 8PM</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12
              }}>
                <SummaryCard
                  icon={<CloudRain size={18} />}
                  label="Rain Next 6h"
                  value={`${forecast?.rain_next_6h_mm?.toFixed(1) || 0} mm`}
                  color="#60a5fa"
                />
                <SummaryCard
                  icon={<CloudRain size={18} />}
                  label="Rain Next 24h"
                  value={`${forecast?.rain_next_24h_mm?.toFixed(1) || 0} mm`}
                  color="#3b82f6"
                />
                <SummaryCard
                  icon={<CloudRain size={18} />}
                  label="Rain Next 48h"
                  value={`${forecast?.rain_next_48h_mm?.toFixed(1) || 0} mm`}
                  color="#2563eb"
                />
              </div>

              {/* Hourly Grid */}
              {Object.entries(groupedByDay).map(([day, hours]) => (
                <div key={day}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--gold)', marginBottom: 10 }}>{day}</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                    gap: 8
                  }}>
                    {hours.map((h, idx) => (
                      <HourCard key={idx} data={h} />
                    ))}
                  </div>
                </div>
              ))}

              {/* Last Updated */}
              {forecast?.fetched_at && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
                  Last updated: {new Date(forecast.fetched_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon, label, value, color = 'var(--gold)' }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--border-soft)',
    borderRadius: 12
  }}>
    <span style={{ color }}>{icon}</span>
    <div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{label}</p>
      <p style={{ fontSize: 15, fontWeight: 600, margin: 0, color }}>{value}</p>
    </div>
  </div>
);

const HourCard = ({ data }) => {
  const time = new Date(data.time);
  const hour = time.toLocaleTimeString('en-US', { hour: '2-digit', hour12: false });
  const v = data.values || {};
  
  const temp = v.temperature?.toFixed(0) || '--';
  const humidity = v.humidity?.toFixed(0) || '--';
  const wind = ((v.windSpeed || 0) * 3.6).toFixed(0);
  const precip = v.precipitationIntensity || v.precip_mm_hr || 0;
  const isRaining = precip >= 0.2;

  // Temperature color gradient (cold blue -> warm orange/red)
  const tempNum = parseFloat(temp);
  const getTempColor = () => {
    if (isNaN(tempNum)) return 'var(--text)';
    if (tempNum <= 0) return '#60a5fa'; // blue
    if (tempNum <= 10) return '#38bdf8'; // light blue
    if (tempNum <= 20) return '#fbbf24'; // yellow
    if (tempNum <= 30) return '#fb923c'; // orange
    return '#ef4444'; // red (hot)
  };

  // Rain intensity color
  const getRainColor = () => {
    if (precip < 0.5) return '#60a5fa'; // light
    if (precip < 2) return '#3b82f6'; // moderate
    if (precip < 5) return '#2563eb'; // heavy
    return '#1d4ed8'; // very heavy
  };

  return (
    <div 
      data-testid={`hour-card-${hour}`}
      style={{
        padding: '10px 8px',
        background: isRaining ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isRaining ? 'rgba(59,130,246,0.4)' : 'var(--border-soft)'}`,
        borderRadius: 10,
        textAlign: 'center'
      }}
    >
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>{hour}:00</p>
      
      {/* Temperature with color */}
      <p style={{ fontSize: 20, fontWeight: 700, marginBottom: 2, color: getTempColor() }}>{temp}°C</p>
      <p style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Temp</p>
      
      {/* Humidity */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
        <Droplets size={12} style={{ color: '#38bdf8' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#38bdf8' }}>{humidity}%</span>
      </div>
      <p style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Humidity</p>
      
      {/* Wind */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
        <Wind size={12} style={{ color: '#a78bfa' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#a78bfa' }}>{wind} km/h</span>
      </div>
      <p style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Wind</p>
      
      {/* Rain if present */}
      {isRaining && (
        <div style={{ 
          marginTop: 6, 
          padding: '4px 8px', 
          background: 'rgba(59,130,246,0.2)', 
          borderRadius: 6,
          display: 'inline-block'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CloudRain size={12} style={{ color: getRainColor() }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: getRainColor() }}>{precip.toFixed(1)} mm</span>
          </div>
          <p style={{ fontSize: 8, color: 'var(--text-muted)', marginTop: 2, textTransform: 'uppercase' }}>Rain</p>
        </div>
      )}
    </div>
  );
};

export default Forecast48hModal;
